<template>
  <vs-popup 
    :title="$t('InviteUserTitle')" 
    :active.sync="isActive" 
    class="invite-user-modal"
    @close="resetForm">
    
    <div class="invite-user-content">
      <div class="vx-row">
        <div class="vx-col w-full">
          <vs-input
            v-model="formData.email"
            :placeholder="$t('EmailPlaceholderUser')"
            type="email"
            class="w-full mb-4"
            :label="$t('EmailAddressLabel')"
            :danger="formErrors.email"
            :danger-text="formErrors.email">
          </vs-input>
        </div>
      </div>

      <div class="vx-row">
        <div class="vx-col w-full">
          <div class="chip-container">
            <v-select 
              multiple
              label="name"
              :closeOnSelect="false"
              v-model="formData.roles"
              taggable
              push-tags
              :options="availableRoles"
              :placeholder="$t('SelectRoles')"
              class="w-full mb-4"
              :class="{ 'is-invalid': formErrors.roleId }"
              @option:created="createRole">
            </v-select>
            <div v-if="formErrors.roleId" class="text-danger text-sm mt-1">{{ formErrors.roleId }}</div>
          </div>
        </div>
      </div>

              <div class="vx-row">
                <div class="vx-col w-full">
                  <vs-textarea
                    v-model="formData.customMessage"
                    :placeholder="$t('CustomMessagePlaceholder')"
                    class="w-full mb-4"
                    :label="$t('CustomMessageLabel')"
                    rows="3">
                  </vs-textarea>
                </div>
              </div>

              <div class="vx-row">
                <div class="vx-col w-1/2">
                  <vs-input
                    v-model="formData.expirationHours"
                    type="number"
                    :label="$t('LinkExpirationLabel')"
                    :placeholder="$t('ExpirationPlaceholder')"
                    class="w-full mb-4">
                  </vs-input>
                </div>
                <div class="vx-col w-1/2">
                  <div class="mb-4">
                    <vs-checkbox
                      v-model="formData.allowRegistration"
                      class="mt-6">
                      {{ $t('AllowUserRegistration') }}
                    </vs-checkbox>
                  </div>
                </div>
              </div>

      <div class="vx-row">
        <div class="vx-col w-full">
          <div class="flex justify-end space-x-2">
            <vs-button 
              color="danger" 
              type="flat"
              @click="closeModal">
              {{ $t('Cancel') }}
            </vs-button>
            <vs-button 
              color="primary"
              :disabled="!isFormValid || loading"
              @click="sendInvitation">
              <span v-if="loading">{{ $t('Sending') }}</span>
              <span v-else>{{ $t('SendInvitation') }}</span>
            </vs-button>
          </div>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import TCVelikClient from '@/TCVelikClient/TCVelikClient';
import vSelect from "vue-select";

export default {
  name: 'InviteUserModal',
  props: {
    active: {
      type: Boolean,
      default: false
    },
    prefilledEmail: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      formData: {
        email: '',
        roles: [],
        customMessage: '',
        expirationHours: 120, // Default 5 days
        allowRegistration: true
      },
      formErrors: {
        email: '',
        roleId: ''
      },
      availableRoles: [],
      loading: false,
      creatingRole: false
    };
  },
  computed: {
    isActive: {
      get() {
        return this.active;
      },
      set(value) {
        this.$emit('update:active', value);
      }
    },
    isFormValid() {
      return this.formData.email && 
             this.formData.roles && 
             this.formData.roles.length > 0 &&
             this.isValidEmail(this.formData.email) &&
             !this.formErrors.email &&
             !this.formErrors.roleId;
    }
  },
  watch: {
    active(newVal) {
      if (newVal) {
        this.loadRoles();
        this.resetForm();
      }
    },
    prefilledEmail(newVal) {
      if (newVal) {
        this.formData.email = newVal;
      }
    }
  },
  methods: {
    async loadRoles() {
      try {
        const query = `
          query RolesWithUsers {
            rolesWithUsers {
              id
              name
              normalizedName
              concurrencyStamp
            }
          }
        `;

        const req = {
          query
        };

        const response = await TCVelikClient.PostRelative("/api/graphql", req, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }
        });

        if (response && response.data && response.data.data) {
          this.availableRoles = response.data.data.rolesWithUsers;
        }
      } catch (error) {
        console.error('Error loading roles:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.$t('FailedToLoadRoles'),
          color: 'danger'
        });
      }
    },

    async createRole(newRoleName) {
      this.creatingRole = true;
      try {
        const query = `
          mutation AddRole($roleName:String!) {
            addRole(roleName: $roleName, roleId: null) {
              id
              name
            }
          }
        `;

        const variables = {
          roleName: newRoleName.name
        };

        const req = {
          query,
          variables
        };

        const response = await TCVelikClient.PostRelative("/api/graphql", req, {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          }
        });

        if (response && response.data && response.data.data) {
          this.$vs.notify({
            title: this.$t('RoleCreatedSuccessfully'),
            color: "success",
            iconPack: "feather",
            position: "top-center",
            icon: "icon-check-circle",
          });

          // Add the new role to available roles
          this.availableRoles.push(response.data.data.addRole);
          
          // Update the formData.roles array to include the new role
          const newRole = response.data.data.addRole;
          this.formData.roles[this.formData.roles.length - 1] = newRole;
          
          // Small delay to ensure role is committed to database
          await new Promise(resolve => setTimeout(resolve, 500));
        } else if (response && response.data && response.data.errors && response.data.data.errors[0].message) {
          this.$vs.notify({
            title: this.$t('RoleAlreadyExists'),
            color: "warning",
            iconPack: "feather",
            position: "top-center",
            icon: "icon-alert-circle",
          });
        } else if (response && response.data && response.data.errors) {
          throw new Error(this.$t('FailedToCreateRole'));
        }
      } catch (error) {
        console.error('Error creating role:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: this.$t('FailedToCreateRole'),
          color: 'danger'
        });
      } finally {
        this.creatingRole = false;
      }
    },
    
    async sendInvitation() {
      this.loading = true;
      this.clearErrors();

      try {
        // Wait for any role creation to complete
        if (this.creatingRole) {
          // Wait for role creation to finish
          while (this.creatingRole) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        // Send single invitation with all selected roles
        const response = await TCVelikClient.PostRelative('/api/admin/InviteUser', {
          Email: this.formData.email,
          RoleIds: this.formData.roles.map(role => role.id),
          CustomMessage: this.formData.customMessage || null,
          ExpirationHours: this.formData.expirationHours,
          AllowRegistration: this.formData.allowRegistration
        });
        
        if (response.data.Success) {
          this.$vs.notify({
            title: this.$t('Success'),
            text: this.$t('InvitationSentSuccessfully', { count: this.formData.roles.length }),
            color: 'success'
          });
          this.closeModal();
        } else {
          throw new Error(response.data.Message || this.$t('FailedToSendInvitation'));
        }
      } catch (error) {
        console.error('Error sending invitation:', error);
        this.$vs.notify({
          title: this.$t('Error'),
          text: error.response?.data?.message || error.message || this.$t('FailedToSendInvitation'),
          color: 'danger'
        });
      } finally {
        this.loading = false;
      }
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    clearErrors() {
      this.formErrors = {
        email: '',
        roleId: ''
      };
    },

    resetForm() {
      this.formData = {
        email: '',
        roles: [],
        customMessage: '',
        expirationHours: 120,
        allowRegistration: true
      };
      this.clearErrors();
    },

    closeModal() {
      this.isActive = false;
      this.resetForm();
      this.$emit('update:prefilled-email', null);
    }
  },
  components: {
    vSelect
  }
};
</script>

<style scoped>
.invite-user-content {
  padding: 1rem;
}

.invite-user-modal .vs-popup {
  max-width: 500px;
}

.chip-container {
  margin-bottom: 1rem;
}

.text-danger {
  color: #dc3545;
}

.text-sm {
  font-size: 0.875rem;
}

.mt-1 {
  margin-top: 0.25rem;
}
</style>
